import { MODAL_KEYS } from '@/common/constants/modal.keys';
import { TablePagination } from '@/components/pagination/TablePagination';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useModal } from '@/hooks/useModal';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { usePartyStore } from '../../store/usePartyStore';
import { CloseParty } from '../close-party';
import { createColumns } from './columns';

export const PartiesTable: FC = () => {
  const { t } = useTranslation();
  const { isOpen } = useModal(MODAL_KEYS.CLOSE_PARTY);
  const columns = createColumns();
  const [searchParams] = useSearchParams();
  const { parties, partiesLoading, partiesPagination } = usePartyStore();
  const pageSize = Number(searchParams.get('size')) || 10;
  const currentPage = Number(searchParams.get('page')) || 1;

  const table = useReactTable({
    data: parties?.content || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="bg-white rounded-md overflow-auto">
      <ScrollArea className="w-full">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="text-xs font-medium text-gray-800 border-b border-gray-200"
              >
                <TableHead className="w-[50px]">№</TableHead>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {partiesLoading ? (
              [...Array(10)].map((_, index) => (
                <TableRow
                  key={index}
                  className="hover:bg-light border-b border-gray-200 text-gray-600"
                >
                  <TableCell className="h-[52px] text-center">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-8 mx-auto" />
                  </TableCell>
                  {columns.map((_, cellIndex) => (
                    <TableCell key={cellIndex} className="h-[52px]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-28" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-light border-b border-gray-200 text-gray-600"
                >
                  <TableCell className="text-center text-black font-medium">
                    {(currentPage - 1) * pageSize + index + 1}
                  </TableCell>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="h-[52px]">
                      <div className="min-w-28">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-[52px] text-center"
                >
                  {t('no.data')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {partiesPagination?.totalElements! > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(
            (partiesPagination?.totalElements || 0) / pageSize,
          )}
          totalElements={partiesPagination?.totalElements}
          pageSize={pageSize}
        />
      )}
      {isOpen && <CloseParty />}
    </div>
  );
};
